import { useEffect, useState } from 'react';
import { X, Loader2, AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';
import sprayService from '../../services/spray';
import type { ApvmaRegistrationsPreview } from '../../types/spray';
import Button from '../ui/Button';

interface Props {
  chemicalId: number;
  productName: string;
  onClose: () => void;
  onImported: () => void;
}

type Phase = 'loading' | 'pick' | 'importing' | 'done' | 'error';

function ApvmaRegistrationsModal({ chemicalId, productName, onClose, onImported }: Props) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [preview, setPreview] = useState<ApvmaRegistrationsPreview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<{ added: number; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    sprayService.getApvmaRegistrations(chemicalId).then(res => {
      if (cancelled) return;
      if (!res.success || !res.data) {
        setError(res.error?.message || 'Could not load APVMA registrations');
        setPhase('error');
        return;
      }
      setPreview(res.data);
      setPhase('pick');
    });
    return () => { cancelled = true; };
  }, [chemicalId]);

  const toggle = (hostcode: string) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(hostcode)) n.delete(hostcode); else n.add(hostcode);
      return n;
    });
  };

  const selectable = (preview?.situations || []).filter(s => !s.all_imported);
  const allSelected = selectable.length > 0 && selectable.every(s => selected.has(s.hostcode));
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectable.map(s => s.hostcode)));
  };

  const doImport = async () => {
    if (!selected.size) return;
    setPhase('importing');
    setError(null);
    const res = await sprayService.importApvmaRegistrations(chemicalId, [...selected]);
    if (!res.success || !res.data) {
      setError(res.error?.message || 'Import failed');
      setPhase('error');
      return;
    }
    setResult(res.data);
    setPhase('done');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-4">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-neutral-800">
            Import crops from APVMA — {productName}
          </h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 max-h-[65vh] overflow-y-auto">
          {phase === 'loading' && (
            <div className="py-12 text-center">
              <Loader2 className="h-8 w-8 text-purple-600 animate-spin mx-auto mb-3" />
              <p className="text-sm text-neutral-700">Reading the APVMA register…</p>
            </div>
          )}

          {phase === 'pick' && preview && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-700">
                {preview.situations.length} registered crop{preview.situations.length === 1 ? '' : 's'} ·{' '}
                {preview.total_combos} crop/pest combinations. Tick the crops you grow — every registered
                target pest for those is imported. <strong>Rates aren't in the open dataset</strong> — imported
                rows start with no rate; fill them from the label.
              </p>
              {preview.unknown_host_combos > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  {preview.unknown_host_combos} combination(s) reference crops not in the local mirror — run the
                  APVMA sync (Spray admin) if you expect more crops.
                </div>
              )}
              {preview.situations.length === 0 ? (
                <p className="text-sm text-neutral-500 italic">No registered crops found for this product.</p>
              ) : (
                <>
                  <label className="flex items-center gap-2 text-xs font-medium text-neutral-600">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded border-neutral-300" />
                    Select all importable ({selectable.length})
                  </label>
                  <div className="space-y-1.5">
                    {preview.situations.map(s => (
                      <label
                        key={s.hostcode}
                        className={`flex items-center justify-between gap-3 border rounded-lg px-3 py-2 ${
                          s.all_imported ? 'opacity-50 border-neutral-200' : 'border-neutral-200 hover:border-purple-300 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <input
                            type="checkbox"
                            disabled={s.all_imported}
                            checked={selected.has(s.hostcode)}
                            onChange={() => toggle(s.hostcode)}
                            className="rounded border-neutral-300"
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-neutral-800 truncate">{s.crop}</div>
                            <div className="text-xs text-neutral-500">
                              {s.pest_count} pest{s.pest_count === 1 ? '' : 's'}
                              {s.already > 0 && ` · ${s.already} already imported`}
                            </div>
                          </div>
                        </div>
                        {s.all_imported
                          ? <span className="text-xs text-emerald-600 shrink-0">imported</span>
                          : <ChevronRight className="h-4 w-4 text-neutral-300 shrink-0" />}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {phase === 'importing' && (
            <div className="py-12 text-center">
              <Loader2 className="h-8 w-8 text-purple-600 animate-spin mx-auto mb-3" />
              <p className="text-sm text-neutral-700">Importing registrations…</p>
            </div>
          )}

          {phase === 'done' && result && (
            <div className="space-y-3 py-4">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-sm font-medium">
                  {result.added} registration{result.added === 1 ? '' : 's'} added
                </span>
              </div>
              {result.skipped > 0 && (
                <p className="text-xs text-neutral-500">{result.skipped} skipped (already present).</p>
              )}
              <p className="text-sm text-neutral-700">
                Now set rates on the new rows from the label — use “Set rate for selected” on the
                registrations list (one rate usually covers many crops).
              </p>
            </div>
          )}

          {phase === 'error' && (
            <div className="py-8">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900">
                <div className="font-semibold mb-1">Something went wrong</div>
                <div>{error}</div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between gap-2">
          <div className="text-xs text-neutral-500">
            {phase === 'pick' && `${selected.size} crop${selected.size === 1 ? '' : 's'} selected`}
          </div>
          <div className="flex gap-2">
            {phase === 'pick' && (
              <>
                <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={doImport} disabled={selected.size === 0}>
                  Import {selected.size || ''}
                </Button>
              </>
            )}
            {phase === 'done' && (
              <Button variant="primary" size="sm" onClick={() => { onImported(); onClose(); }}>Done</Button>
            )}
            {phase === 'error' && (
              <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApvmaRegistrationsModal;
